import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import AIChatSidebar from "../../components/ai/AIChatSidebar";
import AIChatWindow from "../../components/ai/AIChatWindow";
import {
  createConversation,
  getConversations,
  getConversation,
  deleteConversation,
  sendMessage,
  getCountries,
  uploadDocuments,
  requestAnonymousLawyer,
  requestPresencialLawyer,
} from "../../functions/ai";
import "./AdvogadoAI.css";

export default function AdvogadoAI() {
  const { user } = useSelector((state) => ({ ...state }));

  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Country selection
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showCountryModal, setShowCountryModal] = useState(false);

  // Document upload
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Lawyer request
  const [showLawyerModal, setShowLawyerModal] = useState(false);
  const [lawyerType, setLawyerType] = useState(null);
  const [availableLawyers, setAvailableLawyers] = useState([]);

  // Load countries and conversations
  useEffect(() => {
    loadCountries();
    loadConversations();
  }, []);

  const loadCountries = async () => {
    try {
      const data = await getCountries(user.token);
      setCountries(data);
      // Default to Portugal
      setSelectedCountry(data.find((c) => c.code === "PT") || data[0]);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadConversations = async () => {
    try {
      const data = await getConversations("advogado", user.token);
      setConversations(data);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const handleNewConversation = async () => {
    if (!selectedCountry) {
      setShowCountryModal(true);
      return;
    }

    try {
      const conversation = await createConversation(
        "advogado",
        "Consulta jurídica",
        selectedCountry.name,
        user.token
      );
      setConversations([conversation, ...conversations]);
      setActiveConversation(conversation);
      setMessages([]);
      setUploadedDocs([]);
    } catch (error) {
      console.error("Error creating conversation:", error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    try {
      setActiveConversation(conversation);
      const data = await getConversation(conversation._id, user.token);
      setMessages(data.messages);
      setUploadedDocs(data.conversation.documents || []);
      if (data.conversation.country) {
        const country = countries.find((c) => c.name === data.conversation.country);
        if (country) setSelectedCountry(country);
      }
    } catch (error) {
      console.error("Error loading conversation:", error);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId, user.token);
      setConversations(conversations.filter((c) => c._id !== conversationId));
      if (activeConversation?._id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
        setUploadedDocs([]);
      }
    } catch (error) {
      console.error("Error deleting conversation:", error);
    }
  };

  const handleSendMessage = async (content) => {
    let currentConversation = activeConversation;

    // Create conversation if none exists
    if (!currentConversation) {
      if (!selectedCountry) {
        setShowCountryModal(true);
        return;
      }

      try {
        currentConversation = await createConversation(
          "advogado",
          content.substring(0, 50),
          selectedCountry.name,
          user.token
        );
        setConversations([currentConversation, ...conversations]);
        setActiveConversation(currentConversation);
      } catch (error) {
        console.error("Error creating conversation:", error);
        return;
      }
    }

    // Add user message optimistically
    const tempUserMessage = {
      _id: Date.now().toString(),
      role: "user",
      content,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);
    setIsLoading(true);

    try {
      const response = await sendMessage(
        currentConversation._id,
        content,
        false,
        user.token
      );

      setMessages((prev) => [
        ...prev.filter((m) => m._id !== tempUserMessage._id),
        response.userMessage,
        response.assistantMessage,
      ]);

      if (messages.length === 0) {
        setConversations((prev) =>
          prev.map((c) =>
            c._id === currentConversation._id
              ? { ...c, title: content.substring(0, 50) }
              : c
          )
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((m) => m._id !== tempUserMessage._id));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !activeConversation) return;

    setIsUploading(true);
    try {
      const result = await uploadDocuments(
        activeConversation._id,
        files,
        user.token
      );
      setUploadedDocs((prev) => [...prev, ...result.documents]);
    } catch (error) {
      console.error("Error uploading documents:", error);
      alert("Erro ao enviar documentos");
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleRequestLawyer = (type) => {
    setLawyerType(type);
    setShowLawyerModal(true);
  };

  const handleConfirmLawyerRequest = async () => {
    if (!activeConversation) return;

    try {
      if (lawyerType === "anonymous") {
        const result = await requestAnonymousLawyer(
          activeConversation._id,
          null,
          null,
          user.token
        );
        alert(
          `Pedido enviado! Custo: €${result.cost}. Tempo estimado: ${result.estimatedWaitTime}`
        );
      } else {
        const result = await requestPresencialLawyer(
          activeConversation._id,
          null,
          null,
          null,
          user.token
        );
        setAvailableLawyers(result.lawyers || []);
      }
    } catch (error) {
      console.error("Error requesting lawyer:", error);
      alert(error.response?.data?.message || "Erro ao solicitar advogado");
    }

    setShowLawyerModal(false);
  };

  const welcomeContent = {
    title: "Advogado AI",
    subtitle: `Assistência jurídica para ${selectedCountry?.name || "seu país"}`,
    suggestions: [
      {
        icon: "📋",
        text: "Analisar um contrato de trabalho",
        prompt:
          "Preciso de ajuda para analisar um contrato de trabalho. Quais são os pontos mais importantes a verificar?",
      },
      {
        icon: "🏠",
        text: "Direitos do inquilino",
        prompt:
          "Quais são os meus direitos como inquilino se o senhorio quiser aumentar a renda?",
      },
      {
        icon: "💼",
        text: "Despedimento ilegal",
        prompt:
          "Fui despedido sem justa causa. O que posso fazer legalmente?",
      },
      {
        icon: "👨‍👩‍👧",
        text: "Direito de família",
        prompt:
          "Preciso de informações sobre o processo de divórcio e guarda de filhos.",
      },
    ],
  };

  // Extra actions for the chat input area
  const extraActions = (
    <div className="advogado_actions">
      {/* Country selector */}
      <button
        className="action_btn country_btn"
        onClick={() => setShowCountryModal(true)}
        title="Alterar país"
      >
        <span className="country_flag">{selectedCountry?.flag || "🌍"}</span>
        <span className="country_name">{selectedCountry?.name || "Selecionar país"}</span>
      </button>

      {/* Document upload */}
      <button
        className="action_btn upload_btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={!activeConversation || isUploading}
        title="Enviar documentos"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01l1.41 1.41L11 14.84V19h2v-4.16l1.59 1.59L16 15.01 12.01 11z" />
        </svg>
        {isUploading ? "Enviando..." : "Documentos"}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />

      {/* Uploaded docs indicator */}
      {uploadedDocs.length > 0 && (
        <span className="docs_badge">{uploadedDocs.length} doc(s)</span>
      )}

      {/* Request lawyer buttons */}
      <div className="lawyer_actions">
        <button
          className="action_btn lawyer_btn anonymous"
          onClick={() => handleRequestLawyer("anonymous")}
          disabled={!activeConversation}
          title="Advogado anónimo online"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
          </svg>
          Advogado Anónimo
        </button>
        <button
          className="action_btn lawyer_btn presencial"
          onClick={() => handleRequestLawyer("presencial")}
          disabled={!activeConversation}
          title="Advogado presencial"
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Advogado Presencial
        </button>
      </div>
    </div>
  );

  return (
    <div className="advogado_page">
      <Header page="ai" />
      <div className="advogado_container">
        <AIChatSidebar
          conversations={conversations}
          activeConversation={activeConversation}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          type="advogado"
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        />

        <AIChatWindow
          messages={messages}
          onSendMessage={handleSendMessage}
          isLoading={isLoading}
          type="advogado"
          conversation={activeConversation}
          showWelcome={true}
          welcomeContent={welcomeContent}
          inputPlaceholder="Descreva o seu caso jurídico..."
          user={user}
          extraActions={extraActions}
        />
      </div>

      {/* Country Selection Modal */}
      {showCountryModal && (
        <div className="modal_overlay" onClick={() => setShowCountryModal(false)}>
          <div className="modal_content country_modal" onClick={(e) => e.stopPropagation()}>
            <h2>Selecione o País</h2>
            <p>Escolha o país cuja legislação se aplica ao seu caso</p>
            <div className="countries_grid">
              {countries.map((country) => (
                <button
                  key={country.code}
                  className={`country_option ${
                    selectedCountry?.code === country.code ? "selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedCountry(country);
                    setShowCountryModal(false);
                  }}
                >
                  <span className="flag">{country.flag}</span>
                  <span className="name">{country.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Lawyer Request Modal */}
      {showLawyerModal && (
        <div className="modal_overlay" onClick={() => setShowLawyerModal(false)}>
          <div className="modal_content lawyer_modal" onClick={(e) => e.stopPropagation()}>
            <h2>
              {lawyerType === "anonymous"
                ? "Advogado Anónimo"
                : "Advogado Presencial"}
            </h2>

            {lawyerType === "anonymous" ? (
              <>
                <p>
                  Solicite a assistência de um advogado real de forma anónima.
                  O advogado terá acesso ao histórico desta conversa.
                </p>
                <div className="lawyer_info">
                  <div className="info_item">
                    <span className="label">Custo estimado:</span>
                    <span className="value">€50 - €100</span>
                  </div>
                  <div className="info_item">
                    <span className="label">Tempo de resposta:</span>
                    <span className="value">15-30 minutos</span>
                  </div>
                  <div className="info_item">
                    <span className="label">Formato:</span>
                    <span className="value">Chat online</span>
                  </div>
                </div>
                <div className="modal_actions">
                  <button
                    className="cancel_btn"
                    onClick={() => setShowLawyerModal(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="confirm_btn"
                    onClick={handleConfirmLawyerRequest}
                  >
                    Solicitar Advogado
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  Encontre um advogado para consulta presencial na sua área.
                  Preços variam de acordo com o profissional.
                </p>
                {availableLawyers.length > 0 ? (
                  <div className="lawyers_list">
                    {availableLawyers.map((lawyer) => (
                      <div key={lawyer.id} className="lawyer_card">
                        <img src={lawyer.picture} alt={lawyer.name} />
                        <div className="lawyer_info">
                          <h4>{lawyer.name}</h4>
                          <p>{lawyer.specializations?.join(", ")}</p>
                          <span className="rating">
                            ⭐ {lawyer.rating} ({lawyer.reviewCount} avaliações)
                          </span>
                        </div>
                        <div className="lawyer_price">
                          <span>€{lawyer.rate}/hora</span>
                          <button className="select_btn">Selecionar</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="modal_actions">
                    <button
                      className="cancel_btn"
                      onClick={() => setShowLawyerModal(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      className="confirm_btn"
                      onClick={handleConfirmLawyerRequest}
                    >
                      Procurar Advogados
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
